/*
 * Dave Tech: better-ranked search dropdown.
 *
 * Shopify predictive search returns only a handful of products, and for base models ("iphone 14",
 * "s21", "pixel 6") they are usually all the bigger sibling (Pro Max / Ultra / 6a), so the existing
 * re-ranker (dt-search-relevance.js) had nothing correct to promote.
 *
 * This file feeds the dropdown the way the results page is fed: full storefront search through
 * templates/search.dt-predictive.liquid, the same rankResults(), and the same query rewrites when needed.
 * Nothing here is tuned to particular models, colours or words; everything it adapts comes from the
 * products the search returns.
 *
 * Structure:
 *   DaveTechDropdownCore - pure logic, no DOM (also loaded by the test kit so it tests this exact file)
 *   the predictive-search hook - shows today's results, then swaps in the better-ranked list.
 */
(() => {
  const root = typeof window !== "undefined" ? window : globalThis;

  // ---------------------------------------------------------------- core (no DOM) --
  const core = (() => {
    const words = (s) => String(s || "").toLowerCase().split(/[^a-z0-9+]+/).filter(Boolean);
    const normalise = (s) => String(s || "").toLowerCase().trim().replace(/\s+/g, " ");

    // A partly typed last word ("iphone 14 pr") is completed to the most common word with that prefix in
    // the titles the search returned. If the typed word already appears in those titles, it is left alone.
    function completeLastWord(query, items) {
      const parts = query.split(" ");
      if (parts.length < 2) return query;
      const last = parts[parts.length - 1];
      if (!/^[a-z]+$/.test(last)) return query;
      const freq = new Map();
      items.forEach((item) => new Set(words(item.title)).forEach((w) => freq.set(w, (freq.get(w) || 0) + 1)));
      if (freq.has(last)) return query;
      let best = null;
      freq.forEach((n, w) => {
        if (w.startsWith(last) && (!best || n > best.n || (n === best.n && w.length < best.w.length))) best = { w, n };
      });
      if (!best) return query;
      parts[parts.length - 1] = best.w;
      return parts.join(" ");
    }

    // A word typed joined to a model number ("15pro", "s23ultra") is split off when that word appears on its
    // own in the returned titles. Codes such as "x5c" or "a05s" (single trailing letter) are left alone.
    function splitJoinedWords(query, items) {
      const vocab = new Set();
      items.forEach((item) => words(item.title).forEach((w) => vocab.add(w)));
      return query.split(" ").map((token) => {
        const m = token.match(/^([a-z]*\d+)([a-z]{2,})$/);
        return m && vocab.has(m[2]) && !vocab.has(token) ? `${m[1]} ${m[2]}` : token;
      }).join(" ");
    }

    // A query with a model but no brand ("17 pro") takes the brand that dominates Shopify's own top 10
    // results whose titles contain every typed word (at least 3 of them and 60%). Otherwise unchanged.
    function inferBrand(ranker, query, items) {
      if (!ranker.buildIntent) return query;
      const intent = ranker.buildIntent(query);
      if (!intent || !intent.hasModel || intent.skuLike || (intent.brandTokens || []).length) return query;
      // Only results whose titles contain every typed word count ("camon 17 pro" never borrows iPhone),
      // taken in Shopify's relevance order (items arrive in that order).
      const typedWords = words(query);
      const matching = items.filter((item) => { const t = new Set(words(item.title)); return typedWords.every((w) => t.has(w)); }).slice(0, 10);
      if (!matching.length) return query;
      // Each brand word counts separately, so "Samsung A16" and "Samsung Galaxy A16" agree on "samsung".
      const counts = new Map();
      let total = 0;
      matching.forEach((item) => {
        const brands = ranker.buildIntent(item.title).brandTokens || [];
        if (!brands.length) return;
        total += 1;
        brands.forEach((b, i) => { const c = counts.get(b) || { n: 0, pos: 0 }; c.n += 1; c.pos = Math.max(c.pos, i); counts.set(b, c); });
      });
      let top = null; // most frequent; ties go to the more specific word (later in the title, e.g. "iphone" over "apple")
      counts.forEach((c, brand) => { if (!top || c.n > top.n || (c.n === top.n && c.pos > top.pos)) top = { brand, n: c.n, pos: c.pos }; });
      if (!top || top.n < 3 || top.n / total < 0.6) return query;
      return `${top.brand} ${query}`;
    }

    // Variants are titles identical apart from ONE bracketed or " - " section: "(Red)" vs "(Blue)",
    // "- Black" vs "- Green", "(Budget)" vs "(Premium)". Detected from structure, not from a colour list.
    function titleParts(title) {
      const segs = [];
      let base = String(title || "").toLowerCase().replace(/\(([^)]*)\)/g, (m, inner) => { segs.push(inner.trim()); return " "; });
      const dashed = base.split(/\s+[-–]\s+/);
      base = dashed.shift();
      dashed.forEach((s) => segs.push(s.trim()));
      return { base: base.replace(/\s+/g, " ").trim(), segs: segs.filter(Boolean) };
    }
    function isVariantOf(a, b) {
      if (a.base !== b.base || a.segs.length !== b.segs.length || !a.segs.length) return false;
      const rest = [...b.segs];
      let diff = 0;
      a.segs.forEach((s) => { const i = rest.indexOf(s); if (i === -1) diff += 1; else rest.splice(i, 1); });
      return diff === 1;
    }
    // Keep the re-ranker's order, but among results it scores exactly the same (same model, equally good
    // match) show different parts before further variants of a part already shown. A variant never moves
    // below anything the re-ranker scores lower, such as a sibling model.
    function orderVariants(scored) {
      const shown = [];
      return scored
        .map((entry, index) => {
          const parts = titleParts(entry.result.title);
          const earlier = shown.filter((p) => p.tier === entry.tier && p.score === entry.score && isVariantOf(p.parts, parts)).length;
          shown.push({ tier: entry.tier, score: entry.score, parts });
          return { entry, index, earlier };
        })
        .sort((a, b) => (a.entry.tier - b.entry.tier) || (b.entry.score - a.entry.score) || (a.earlier - b.earlier) || (a.index - b.index))
        .map((x) => x.entry.result);
    }

    // More queries are needed until there are `max` results as good as this kind of search can get:
    // exact model + part for "model + part" searches (re-ranker tier 1-2), exact model with every typed
    // qualifier for model-only searches (tier 4 is the best a model-only search can reach), a strong part
    // match for part-only searches. No conflicts with the typed brand, family or 4G/5G.
    function needsExpansion(ranker, results, query, max = 5) {
      if (!results.length) return true;
      const ranked = ranker.rankResults(results, query, { keepAll: true });
      const intent = ranked.intent || {};
      const bestPossible = intent.hasModel ? (intent.hasPart ? 2 : 4) : intent.hasPart ? 2 : Infinity;
      if (bestPossible === Infinity) return results.length < max;
      const good = (ranked.scored || []).filter((it) => it.tier <= bestPossible && !it.brandConflict && !it.familyConflict && !it.networkConflict && !(it.missingQualifiers || []).length);
      return good.length < max;
    }

    // fetchBatch(query) -> Promise<items>. Plain query first (one request); the results page's rewrites
    // only when that finds no exact match.
    async function run(ranker, rawTerm, fetchBatch, max = 5) {
      const typed = normalise(rawTerm);
      const merged = [];
      const seen = new Set();
      const add = (items) => (items || []).forEach((item) => {
        const key = String(item.url || item.title || "").split("?")[0];
        if (key && !seen.has(key)) { seen.add(key); merged.push(item); }
      });
      add(await fetchBatch(typed));
      let query = splitJoinedWords(typed, merged);
      query = completeLastWord(query, merged);
      query = inferBrand(ranker, query, merged);
      const fetched = new Set([typed]);
      if (needsExpansion(ranker, merged, query, max)) {
        // The results page's rewrites of the (completed, brand-aware) query, then that query itself.
        const next = [...((ranker.buildShopifyQueries && ranker.buildShopifyQueries(query)) || []).map((d) => d.query), query]
          .filter((q) => q && !fetched.has(q) && (fetched.add(q), true));
        for (const q of next) {
          add(await fetchBatch(q));
          if (!needsExpansion(ranker, merged, query, max)) break;
        }
      }
      const scored = (ranker.rankResults(merged, query, { keepAll: true }).scored) || [];
      return { query, candidates: merged.length, results: orderVariants(scored).slice(0, max) };
    }

    return { run, splitJoinedWords, completeLastWord, inferBrand, orderVariants, titleParts, isVariantOf, needsExpansion, normalise };
  })();
  root.DaveTechDropdownCore = core;

  // ------------------------------------------------------------ dropdown hook (DOM) --
  const Element = typeof customElements !== "undefined" && customElements.get("predictive-search");
  if (!Element || Element.prototype.__dtFullSearch) return;
  const proto = Element.prototype;
  proto.__dtFullSearch = true;

  const MAX_SHOWN = 5;
  const CACHE_TTL_MS = 30000;
  const HOLD_MS = 700; // hold today's list back this long while the better list loads
  const VIEW = "dt-predictive";
  const cache = new Map();

  function viewUrl(query) {
    const url = new URL(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}search` : "/search", window.location.origin);
    url.searchParams.set("q", query);
    url.searchParams.set("type", "product");
    url.searchParams.set("view", VIEW);
    url.searchParams.set("options[unavailable_products]", "show");
    return url.toString();
  }

  async function fetchBatch(query, signal) {
    for (let attempt = 0; attempt < 2; attempt++) {
      const response = await fetch(viewUrl(query), { signal, credentials: "same-origin", headers: { Accept: "application/json" } });
      if (response.status === 429) { await new Promise((r) => setTimeout(r, 800)); continue; }
      if (!response.ok) throw new Error(String(response.status));
      return (await response.json()).results || [];
    }
    throw new Error("429");
  }

  function ensureList(host) {
    let list = host.querySelector("#predictive-search-results-products-list");
    if (list) return list;
    const results = host.querySelector("#predictive-search-results");
    if (!results) return null;
    // Predictive returned nothing visible (e.g. only trade-only products) - build the products group.
    results.querySelectorAll(".predictive-search__result-group").forEach((el) => el.remove());
    const wrapper = document.createElement("div");
    wrapper.id = "predictive-search-results-groups-wrapper";
    wrapper.className = "predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions predictive-search__results-groups-wrapper--products-only";
    wrapper.innerHTML =
      '<div class="predictive-search__result-group predictive-search__result-group--products-only"><div>' +
      '<h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">Products</h2>' +
      '<ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products"></ul>' +
      "</div></div>";
    results.prepend(wrapper);
    if (!results.querySelector(".predictive-search__item--term")) {
      const term = host.getQuery();
      const link = document.createElement("a");
      link.href = `/search?q=${encodeURIComponent(term)}&type=product`;
      link.className = "predictive-search__item predictive-search__item--term link link--text h5 animate-arrow";
      link.setAttribute("role", "option");
      link.setAttribute("aria-selected", "false");
      link.innerHTML = `<span data-predictive-search-search-for-text></span>`;
      link.querySelector("span").textContent = `Search for “${term}”`;
      wrapper.after(link);
    }
    return wrapper.querySelector("ul");
  }

  function applyResults(host, term, items) {
    if (!items || !items.length || host.searchTerm !== term) return;
    const list = ensureList(host);
    if (!list) return;
    list.innerHTML = items.map((item) => item.html).join("");
    list.querySelectorAll("li[id^='predictive-search-option-product-']").forEach((li, i) => { li.id = `predictive-search-option-product-${i + 1}`; });
    list.dataset.dtFullRanked = "true";
    host.setAttribute("results", true);
    host.open();
  }

  // Do not let the predictive-only re-rank reorder the list after the full results are in.
  const originalRank = proto.rankVisibleProducts;
  proto.rankVisibleProducts = function (searchTerm) {
    const list = this.predictiveSearchResults?.querySelector("#predictive-search-results-products-list");
    if (list?.dataset.dtFullRanked === "true") return;
    return originalRank.call(this, searchTerm);
  };

  const originalRender = proto.renderSearchResults;
  const flushHeld = (host) => {
    if (host.dtHeldTimer) clearTimeout(host.dtHeldTimer);
    host.dtHeldTimer = null;
    const held = host.dtHeld;
    host.dtHeld = null;
    if (held && held.term === host.searchTerm) originalRender.call(host, held.markup);
  };

  proto.renderSearchResults = function (markup) {
    const term = this.searchTerm;
    const cached = cache.get(this.getQueryKey(term));
    const fresh = cached && Date.now() - cached.at < CACHE_TTL_MS;
    const pending = this.dtPending && this.dtPending.term === term;
    if (!fresh && pending) {
      const wait = HOLD_MS - (Date.now() - this.dtPending.at);
      if (wait > 0) {
        if (this.dtHeldTimer) clearTimeout(this.dtHeldTimer);
        this.dtHeld = { term, markup };
        this.dtHeldTimer = setTimeout(() => flushHeld(this), wait);
        return;
      }
    }
    originalRender.call(this, markup);
    if (fresh) applyResults(this, term, cached.items);
  };

  const originalGet = proto.getSearchResults;
  proto.getSearchResults = function (searchTerm) {
    const key = this.getQueryKey(searchTerm);
    const cached = cache.get(key);
    if (this.dtFullAbort) this.dtFullAbort.abort();
    if (this.dtHeldTimer) clearTimeout(this.dtHeldTimer);
    this.dtHeld = null;
    if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
      this.dtPending = null;
      originalGet.call(this, searchTerm); // renderSearchResults applies the cached list
      requestAnimationFrame(() => applyResults(this, searchTerm, cached.items));
      return;
    }
    this.dtPending = { term: searchTerm, at: Date.now() };
    originalGet.call(this, searchTerm); // predictive request, exactly as today (held briefly on render)
    const controller = (this.dtFullAbort = new AbortController());
    this.ensureSearchRelevance()
      .then((ranker) => {
        if (!ranker?.rankResults) return null;
        return core.run(ranker, searchTerm, (q) => fetchBatch(q, controller.signal), MAX_SHOWN).then((r) => r.results);
      })
      .then((items) => {
        if (this.dtPending?.term === searchTerm) this.dtPending = null;
        if (items && items.length) cache.set(key, { items, at: Date.now() });
        if (this.dtHeld && this.dtHeld.term === searchTerm) {
          flushHeld(this); // predictive already arrived: render it and apply the better list in one go
          if (items) applyResults(this, searchTerm, items);
        } else if (items && this.getAttribute("loading") !== "true") {
          applyResults(this, searchTerm, items);
        }
        // otherwise predictive has not rendered yet; renderSearchResults applies the cached list when it does
      })
      .catch((error) => {
        if (this.dtPending?.term === searchTerm) this.dtPending = null;
        if (this.dtHeld && this.dtHeld.term === searchTerm) flushHeld(this);
        if (error?.name !== "AbortError") console.warn("[dt-predictive-full] kept predictive results:", error);
      });
  };
})();
